// ============================================================================
// 카드 레지스트리 — 카드 종류 목록의 정본 (BINDER_DESIGN §3)
//
// 데이터는 tools/build-registry.mjs 가 만든 cards.data.json 이다. **손으로 고치지 마라.**
// Vite 가 JSON 을 번들에 넣으므로 동기 로드다 — 로딩 화면 없음 규칙을 지키려고 그렇게 했다.
//
// 이 파일은 **params.ts 를 import 하지 않는다.** params.ts 가 rollCards() 에서
// DEFS_BY_RANK 를 쓰기 때문에 서로 import 하면 순환이 되고, 먼저 로드되는 쪽에 따라
// TDZ 오류가 난다. 대신 params.ts 가 자기 초기화 끝에 setFaceShape(P.binder) 로
// 살아 있는 객체를 넘겨 준다.
// ============================================================================
import data from './cards.data.json' with { type: 'json' };

export interface CardDef {
  id: string; // 영구 식별자. 절대 바꾸지 않는다. 저장 데이터의 키
  set: string; // 세트 코드. 지금은 표시·배치에 안 쓴다 (세트 구역(B) 를 위해 데이터에만 둔다)
  no: number; // 세트 안 도감번호
  rank: number; // 등급 0..3 (CARD_KINDS 인덱스와 같은 의미)
  slot: number; // 바인더 슬롯 1..9F. 빌드 도구가 배정한다. 저장 데이터는 이 값을 모른다
  name?: string; // 표시 이름. 없으면 id 를 보여준다
  art?: string; // 그림 파일 (assets 루트 기준). 없으면 관례대로 cards/<id>.webp
}

export const CELLS_PER_FACE = 9;
export const FACES_PER_SHEET = 2;

/**
 * cardgen 출력(1024×1432) 종횡비. **카드 그림이 있는 곳은 전부 이걸 쓴다** — 바인더의 칸·상세
 * 보기뿐 아니라 개봉 화면의 CardMesh 치수(params 의 BASE.cardH)도 여기서 나온다.
 * 등급 폴백 그림(card_common.webp ...)도 같은 규격으로 다시 뽑았다.
 */
export const CARD_ASPECT: number = (data as any).meta?.cardAspect ?? 1432 / 1024;

// 배열·맵은 **자리를 유지**한다 (import 한 쪽이 계속 같은 참조를 본다).
// overrideRegistry() 가 내용만 갈아끼운다 — 규모 검증 도구가 가짜 레지스트리를 주입한다.
export const CARD_DEFS: CardDef[] = [];
export const CARD_BY_ID = new Map<string, CardDef>();
export const SLOT_TO_DEF = new Map<number, CardDef>();
export const DEFS_BY_RANK: CardDef[][] = [[], [], [], []];

let maxSlot = 0;

/** 면 수 계산에 쓰는 튜닝 값. params.ts 가 로드되면 P.binder 로 교체된다 (위 주석 참고). */
let faceShape: { minFaces: number; spareFaces: number } = { minFaces: 10, spareFaces: 4 };
export function setFaceShape(s: { minFaces: number; spareFaces: number }): void {
  faceShape = s;
}

function loadDefs(list: readonly CardDef[]): void {
  CARD_DEFS.length = 0;
  CARD_BY_ID.clear();
  SLOT_TO_DEF.clear();
  for (const r of DEFS_BY_RANK) r.length = 0;
  maxSlot = 0;

  for (const raw of list) {
    const d: CardDef = { id: raw.id, set: raw.set, no: raw.no, rank: raw.rank | 0, slot: raw.slot | 0, name: raw.name, art: raw.art };
    // 검사는 console.error 로만 — 게임이 죽으면 안 된다 (§3.2)
    if (!d.id) {
      console.error('[cards] id 가 없는 항목', raw);
      continue;
    }
    if (CARD_BY_ID.has(d.id)) {
      console.error(`[cards] id 중복: ${d.id}`);
      continue;
    }
    if (!(d.slot >= 1)) {
      console.error(`[cards] ${d.id} 의 slot 이 범위 밖: ${d.slot}`);
      continue;
    }
    if (SLOT_TO_DEF.has(d.slot)) {
      console.error(`[cards] slot 중복: ${d.slot} (${SLOT_TO_DEF.get(d.slot)!.id} / ${d.id})`);
      continue;
    }
    if (!(d.rank >= 0 && d.rank <= 3)) {
      console.error(`[cards] ${d.id} 의 rank 가 범위 밖: ${d.rank}`);
      continue;
    }
    CARD_DEFS.push(d);
    CARD_BY_ID.set(d.id, d);
    SLOT_TO_DEF.set(d.slot, d);
    DEFS_BY_RANK[d.rank].push(d);
    if (d.slot > maxSlot) maxSlot = d.slot;
  }
  CARD_DEFS.sort((a, b) => a.slot - b.slot);
  for (let r = 0; r < 4; r++) {
    if (DEFS_BY_RANK[r].length === 0) {
      // 데이터가 아직 안 채워진 것이지 코드 오류가 아니다 — 그 등급이 뽑히면 defIds 가 null 이고
      // 커밋에서 무시된다. 빌드 시점 경고는 tools/build-registry.mjs 가 낸다.
      console.info(`[cards] 등급 ${r} 에 카드가 0장이다 — 그 등급이 뽑히면 바인더에 아무것도 안 들어간다`);
    }
  }
}

loadDefs((data as any).cards ?? []);

/** 검증 도구용 가짜 레지스트리 주입 (리로드 전까지만). */
export function overrideRegistry(list: readonly CardDef[]): number {
  loadDefs(list);
  return CARD_DEFS.length;
}

/** 카드 그림의 경로 (assets 루트 기준). art 가 없으면 관례대로 cards/<id>.webp — 카드당 파일 하나다. */
export function cardArtPath(d: CardDef): string {
  return d.art ? d.art : `cards/${d.id}.webp`;
}

export function cardById(id: string): CardDef | undefined {
  return CARD_BY_ID.get(id);
}
export function cardBySlot(slot: number): CardDef | undefined {
  return SLOT_TO_DEF.get(slot);
}
export function maxAssignedSlot(): number {
  return maxSlot;
}

/**
 * 면 수. **어디에도 고정 페이지 수를 하드코딩하지 않는다** (§3.3).
 *   F = 2·ceil( max(ceil(maxSlot/9) + spareFaces, minFaces) / 2 )
 * 시트가 양면이라 항상 짝수다.
 */
export function faceCount(): number {
  const needed = Math.ceil(maxSlot / CELLS_PER_FACE);
  const want = Math.max(needed + Math.max(0, faceShape.spareFaces | 0), Math.max(2, faceShape.minFaces | 0));
  return 2 * Math.ceil(want / 2);
}
export function sheetCount(): number {
  return faceCount() / FACES_PER_SHEET;
}

export function slotToFace(slot: number): number {
  return Math.ceil(slot / CELLS_PER_FACE);
}
export function slotToCell(slot: number): number {
  return (slot - 1) % CELLS_PER_FACE;
}
export function faceToSlot(face: number, cell: number): number {
  return (face - 1) * CELLS_PER_FACE + cell + 1;
}
export function faceToSheet(face: number): number {
  return Math.ceil(face / FACES_PER_SHEET);
}
/** 홀수 면 = 시트의 앞면(오른쪽 더미 위), 짝수 면 = 뒷면(왼쪽 더미 위). */
export function faceIsBack(face: number): boolean {
  return face % 2 === 0;
}

export interface CellInfo {
  slot: number;
  def: CardDef | undefined;
  count: number;
}

/** 면 f 의 9칸. 조회 9회, 할당은 out 배열을 재사용하면 없다 (§7.1). */
export function cellsOfFace(face: number, counts: Record<string, number>, out: CellInfo[] = []): CellInfo[] {
  out.length = 0;
  for (let i = 0; i < CELLS_PER_FACE; i++) {
    const slot = faceToSlot(face, i);
    const def = SLOT_TO_DEF.get(slot);
    out.push({ slot, def, count: def ? counts[def.id] ?? 0 : 0 });
  }
  return out;
}
